import React, { useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import { toast } from "react-toastify";
import { apiTicketingGetSettings, apiTicketingUpdateSettings } from "../api";
import { Page, Hero, Panel, Field, PInput, PButton, S, C } from "../theme/pbi";
import HoldPolicyPanel from "../components/ticketing/HoldPolicyPanel";
import SettingHelp from "./Settings/settingsHelp";
import { ReadOnlyNotice } from "./Settings/settingsUi";

/* Where the switch's value comes from (server ticketingSettingsService). */
const SWITCH_SOURCE = {
  saved: "Saved on this page",
  env: "Set in the server environment (TICKETING_ENABLED); nothing saved here yet",
  default: "Server default (off); nothing saved here yet",
};

const TicketingSettings = ({ canEdit = false }) => {
  const [enabled, setEnabled] = useState(false);
  const [countries, setCountries] = useState("");
  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await apiTicketingGetSettings();
      setCurrent(data);
      setEnabled(!!data.enabled);
      setCountries(Array.isArray(data.countries) ? data.countries.join(", ") : "");
    } catch (err) {
      toast.error("Could not load ticketing settings.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const list = countries.split(",").map((s) => s.trim().toUpperCase()).filter(Boolean);
      const { data } = await apiTicketingUpdateSettings({ enabled, countries: list });
      setCurrent(data);
      setEnabled(!!data.enabled);
      setCountries(Array.isArray(data.countries) ? data.countries.join(", ") : "");
      toast.success(`Ticketing is now ${data.enabled ? "ON" : "OFF"}.`);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not save. Only a super administrator can change ticketing settings.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Page>
      <div style={{ padding: "16px 12px 0" }}>
        <Hero
          title="Ticketing (pilot)"
          subtitle="Whether customers can buy event tickets on the web and in the apps, in which markets, and how long picked seats stay reserved. Turning ticketing off stops new online sales; tickets already issued stay valid and scannable."
          right={<span style={S.chip(current?.enabled)}>{current?.enabled ? "● LIVE" : "○ OFF"}</span>}
        />
        <div style={{ maxWidth: 1040 }}>
          <Panel title="Ticketing on or off" sub={SWITCH_SOURCE[current?.source] || null} style={{ maxWidth: 640 }}>
            {!canEdit && <ReadOnlyNotice level="manage" />}
            {loading ? (
              <div style={{ color: C.textLight }}>Loading…</div>
            ) : (
              <form onSubmit={save}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "2px 0 18px" }}>
                  <div>
                    <div style={{ fontWeight: 700, color: C.text }}>Ticketing is live for customers<SettingHelp topic="ticketingPilotSwitch" /></div>
                    <div style={{ fontSize: 12, color: C.textLight }}>Shows the Events tile and allows new seat holds and checkouts on the web and in the apps.</div>
                  </div>
                  <Form.Check type="switch" id="tk-enabled" checked={enabled} disabled={!canEdit} onChange={(e) => setEnabled(e.target.checked)} />
                </div>
                <Field label={<>Limit to these markets (optional)<SettingHelp topic="ticketingEnabledCountries" /></>} hint="Two letter market codes separated by commas, for example UG, ZM. Leave blank to sell in every market while the switch above is on.">
                  <PInput value={countries} disabled={!canEdit} onChange={(e) => setCountries(e.target.value)} placeholder="Leave blank for all countries" />
                </Field>
                <div style={{ background: C.greenSoft, border: "1px solid #c8e6c9", color: C.green, borderRadius: 8, padding: "11px 14px", fontSize: 12.5, margin: "4px 0 18px", lineHeight: 1.5 }}>
                  Turning ticketing <strong>off</strong> stops new online sales and hides the tile, but never invalidates a ticket already issued. Box office sales on a POS terminal and partner seat blocks are not stopped by this switch.
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                  {canEdit && <PButton type="submit" loading={saving}>Save changes</PButton>}
                  {current?.default != null && (
                    <span style={{ fontSize: 12, color: C.textLight }}>With nothing saved, ticketing is {current.default ? "on" : "off"} (the server default).</span>
                  )}
                </div>
              </form>
            )}
          </Panel>

          {/* How long a seat stays reserved: platform default and per-organizer
              overrides, inside the super admin's allowed range. */}
          <HoldPolicyPanel />
        </div>
      </div>
    </Page>
  );
};

export default TicketingSettings;
